import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const api = axios.create({
  baseURL: API,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add auth token to requests if available in localStorage
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('session_token');
    if (token) {
      config.headers['Authorization'] = `Bearer ${token}`;
      console.log('Adding auth header to request:', config.url);
    } else {
      console.log('No token in localStorage for request:', config.url);
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  signup: (userData) => api.post('/auth/signup', userData),
  signin: (credentials) => api.post('/auth/signin', credentials),
  signout: () => api.post('/auth/signout'),
  getSession: () => api.get('/auth/session'),
};

// Content API
export const contentAPI = {
  getAll: (params) => api.get('/content', { params }),
  getById: (id) => api.get(`/content/${id}`),
  getEpisodes: (id, season) => api.get(`/content/${id}/episodes`, { params: { season } }),
  search: (query, contentType) => api.get('/content/search/query', { params: { q: query, content_type: contentType } }),
};

// Favorites API
export const favoritesAPI = {
  add: (contentId) => api.post('/favorites', { content_id: contentId }),
  remove: (contentId) => api.delete(`/favorites/${contentId}`),
  getAll: () => api.get('/favorites'),
  check: (contentId) => api.get(`/favorites/check/${contentId}`),
};

// Comments API
export const commentsAPI = {
  create: (commentData) => api.post('/comments', commentData),
  getAll: (contentId, sort = 'newest') => api.get(`/comments/${contentId}`, { params: { sort } }),
  update: (commentId, text) => api.put(`/comments/${commentId}`, { text }),
  delete: (commentId) => api.delete(`/comments/${commentId}`),
  like: (commentId, isLike) => api.post(`/comments/${commentId}/like`, null, { params: { is_like: isLike } }),
  flag: (commentId) => api.post(`/comments/${commentId}/flag`),
};

// Ratings API
export const ratingsAPI = {
  submit: (contentId, rating) => api.post('/ratings', { content_id: contentId, rating }),
  getUserRating: (contentId) => api.get(`/ratings/${contentId}/user`),
};

// Watch Progress API
export const watchProgressAPI = {
  update: (progressData) => api.post('/watch-progress', progressData),
  getContinueWatching: () => api.get('/watch-progress'),
  getProgress: (contentId, episodeId) => api.get(`/watch-progress/${contentId}`, { params: { episode_id: episodeId } }),
  remove: (contentId) => api.delete(`/watch-progress/${contentId}`),
};

// User Profile API
export const userAPI = {
  getProfile: () => api.get('/users/profile'),
  updateProfile: (data) => api.put('/users/profile', null, { params: data }),
  getComments: (userId) => api.get(`/users/${userId}/comments`),
  getRatings: (userId) => api.get(`/users/${userId}/ratings`),
};

// Payments API
export const paymentsAPI = {
  createSubscriptionCheckout: () => api.post('/payments/create-subscription-checkout'),
  createPPVCheckout: (contentId) => api.post(`/payments/create-ppv-checkout/${contentId}`),
  getCheckoutStatus: (sessionId) => api.get(`/payments/checkout-status/${sessionId}`),
  getSubscriptionStatus: () => api.get('/payments/subscription-status'),
  cancelSubscription: () => api.post('/payments/cancel-subscription'),
  resumeSubscription: () => api.post('/payments/resume-subscription'),
  openBillingPortal: () => api.post('/payments/billing-portal'),
  checkPPVAccess: (contentId) => api.get(`/payments/ppv-access/${contentId}`),
  getMyPurchases: () => api.get('/payments/my-purchases'),
};

export default api;
